import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { organizationStore } from 'src/organization/store';

import type {
  ActionMapType,
  AuthStateType,
  AuthUserType,
  JWTContextType,
} from './types';

// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { isValidToken, setSession } from './utils';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  REGISTER = 'REGISTER',
}

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

type Payload = {
  [Types.INITIAL]: {
    isAuthenticated: boolean;
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.REGISTER]: {
    user: AuthUserType;
  };
};

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      isAuthenticated: action.payload.isAuthenticated,
      isInitialized: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable
        ? localStorage.getItem('accessToken')
        : '';
      const refreshToken = storageAvailable
        ? localStorage.getItem('refreshToken')
        : '';

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken, refreshToken);
      } else if (refreshToken) {
        const response = await axios.post('/token/refresh/', {
          refresh: refreshToken,
        });
        setSession(response.data.access, refreshToken);
      } else {
        dispatch({
          payload: {
            isAuthenticated: false,
            user: null,
          },
          type: Types.INITIAL,
        });
        return;
      }

      const response = await axios.get('/core/current-user/');

      dispatch({
        payload: {
          isAuthenticated: true,
          user: response.data,
        },
        type: Types.INITIAL,
      });
    } catch (error) {
      console.error(error);
      dispatch({
        payload: {
          isAuthenticated: false,
          user: null,
        },
        type: Types.INITIAL,
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    let response = await axios.post('/token/obtain/', {
      email,
      password,
    });
    const { access, refresh } = response.data;

    setSession(access, refresh);

    response = await axios.get('/core/current-user/');

    dispatch({
      payload: {
        user: response.data,
      },
      type: Types.LOGIN,
    });
  }, []);

  // REGISTER
  const register = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string
    ) => {
      const response = await axios.post('/api/account/register', {
        email,
        firstName,
        lastName,
        password,
      });
      const { accessToken, user } = response.data;

      localStorage.setItem('accessToken', accessToken);

      dispatch({
        payload: {
          user,
        },
        type: Types.REGISTER,
      });
    },
    []
  );

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null, null);
    dispatch({
      type: Types.LOGOUT,
    });
    organizationStore.getState().unsetActiveCompany();
    window.location.reload();
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isAuthenticated: state.isAuthenticated,
      isInitialized: state.isInitialized,
      login,
      loginWithGithub: () => {},
      loginWithGoogle: () => {},
      loginWithTwitter: () => {},
      logout,
      method: 'jwt',
      register,
      user: state.user,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      login,
      logout,
      register,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
};
